/**
 * Due-date calendar.
 *
 * A 7×5 grid on desktop; a day-by-day list on a phone. A month grid at 375px
 * gives each cell about 50 pixels, which cannot hold a date and an amount
 * legibly — shrinking it would technically "work" and be useless.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/misc';
import { EmptyState, ErrorState } from '@/components/common';
import { PayInstalmentDialog } from '@/components/PayInstalmentDialog';
import { colorClasses } from '@/lib/colors';
import { formatDate, formatMonth, formatMoney, todayIso } from '@/lib/format';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { usePlanCalendar } from '@/hooks/useApi';
import type { UpcomingInstalment } from '@/lib/types';

const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'нд'];

function addMonthKey(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const total = y * 12 + (m - 1) + delta;
  return `${Math.floor(total / 12)}-${String((((total % 12) + 12) % 12) + 1).padStart(2, '0')}`;
}

export function PlanCalendarPage(): JSX.Element {
  const [month, setMonth] = useState(() => todayIso().slice(0, 7));
  const [paying, setPaying] = useState<UpcomingInstalment | null>(null);
  const isMobile = useIsMobile();

  const { data, isLoading, error, refetch } = usePlanCalendar(month);

  const byDate = useMemo(() => {
    const map = new Map<string, UpcomingInstalment[]>();
    for (const d of data?.days ?? []) map.set(d.date, d.items);
    return map;
  }, [data]);

  /** Monday-first grid, padded to whole weeks. */
  const grid = useMemo(() => {
    const [y, m] = month.split('-').map(Number) as [number, number];
    const first = new Date(Date.UTC(y, m - 1, 1));
    const lead = (first.getUTCDay() + 6) % 7; // Monday = 0
    const days = new Date(Date.UTC(y, m, 0)).getUTCDate();

    const cells: Array<{ date: string | null }> = [];
    for (let i = 0; i < lead; i++) cells.push({ date: null });
    for (let d = 1; d <= days; d++) {
      cells.push({ date: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
    }
    while (cells.length % 7 !== 0) cells.push({ date: null });
    return cells;
  }, [month]);

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const base = data?.baseCurrency ?? 'EUR';
  const dec = data?.baseDecimals ?? 2;
  const today = todayIso();
  const monthTotal = (data?.days ?? [])
    .flatMap((d) => d.items)
    .filter((i) => i.direction === 'EXPENSE')
    .reduce((n, i) => n + i.baseAmountMinor, 0);

  return (
    <div>
      <Button variant="ghost" size="sm" asChild className="-ml-2 mb-2 gap-1 text-muted-foreground">
        <Link to="/plan">
          <ArrowLeft className="h-4 w-4" />
          План
        </Link>
      </Button>

      <div className="mb-4 flex items-center justify-between gap-2">
        <Button
          variant="outline"
          size="icon-sm"
          onClick={() => setMonth((m) => addMonthKey(m, -1))}
          aria-label="Предишен месец"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="text-center">
          <h1 className="text-lg font-semibold">{formatMonth(month)}</h1>
          {monthTotal > 0 ? (
            <p className="tabular text-xs text-muted-foreground">
              {formatMoney(monthTotal, base, dec)} задължения
            </p>
          ) : null}
        </div>
        <Button
          variant="outline"
          size="icon-sm"
          onClick={() => setMonth((m) => addMonthKey(m, 1))}
          aria-label="Следващ месец"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-96 w-full rounded-lg" />
      ) : (data?.days.length ?? 0) === 0 ? (
        <Card>
          <EmptyState icon={CalendarDays} title="Няма падежи този месец" />
        </Card>
      ) : isMobile ? (
        <div className="space-y-3">
          {(data?.days ?? []).map((day) => (
            <div key={day.date}>
              <h2
                className={cn(
                  'mb-1.5 text-sm font-medium',
                  day.date === today && 'text-primary',
                  day.date < today && 'text-muted-foreground',
                )}
              >
                {formatDate(day.date)}
                {day.date === today ? ' · днес' : ''}
              </h2>
              <div className="space-y-1.5">
                {day.items.map((i) => (
                  <InstalmentRow key={i.id} item={i} onPay={() => setPaying(i)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-3">
            <div className="mb-1 grid grid-cols-7 gap-1">
              {WEEKDAYS.map((w) => (
                <div key={w} className="py-1 text-center text-xs font-medium text-muted-foreground">
                  {w}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {grid.map((cell, idx) => {
                if (!cell.date) return <div key={idx} className="min-h-24 rounded-md" />;
                const items = byDate.get(cell.date) ?? [];
                const isToday = cell.date === today;
                return (
                  <div
                    key={cell.date}
                    className={cn(
                      'min-h-24 rounded-md border p-1.5 transition-colors',
                      isToday && 'border-primary/50 bg-primary/5',
                      items.length === 0 && 'opacity-60',
                    )}
                  >
                    <div
                      className={cn(
                        'tabular mb-1 text-xs',
                        isToday ? 'font-semibold text-primary' : 'text-muted-foreground',
                      )}
                    >
                      {Number(cell.date.slice(8, 10))}
                    </div>
                    <div className="space-y-1">
                      {items.map((i) => (
                        <button
                          key={i.id}
                          type="button"
                          onClick={() => setPaying(i)}
                          title={`${i.name} · ${formatMoney(i.amountMinor, i.currency, i.decimals)}`}
                          className={cn(
                            'block w-full truncate rounded px-1 py-0.5 text-left text-[10px] transition-opacity hover:opacity-80',
                            i.direction === 'INCOME'
                              ? 'bg-income/15 text-income'
                              : i.overdue
                                ? 'bg-destructive/20 text-destructive'
                                : 'bg-expense/15 text-expense',
                          )}
                        >
                          {i.name}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <PayInstalmentDialog instalment={paying} onClose={() => setPaying(null)} />
    </div>
  );
}

function InstalmentRow({
  item,
  onPay,
}: {
  item: UpcomingInstalment;
  onPay: () => void;
}): JSX.Element {
  const colors = colorClasses(item.color);
  return (
    <button
      type="button"
      onClick={onPay}
      className={cn(
        'flex w-full items-center gap-3 rounded-md border p-2.5 text-left',
        item.overdue && 'border-destructive/40',
      )}
    >
      <span className={cn('h-2 w-2 shrink-0 rounded-full', colors.bg)} />
      <span className="min-w-0 flex-1 truncate text-sm">{item.name}</span>
      <span
        className={cn(
          'tabular shrink-0 text-sm',
          item.direction === 'INCOME' ? 'text-income' : 'text-expense',
        )}
      >
        {item.direction === 'INCOME' ? '+' : '−'}
        {formatMoney(item.amountMinor, item.currency, item.decimals)}
      </span>
    </button>
  );
}
